import Customizer from './customizer.js';
import events from './event.js';
import _ from './translate.js';
import { textString, textObj, imageUrl, getNumber, getBoolean } from './texts.js';
import TextParser from './textParser.js';
import Logger from './logger.js';
import Storage from './storage.js';
import CoverOverlay from './coverOverlay.js';
import CoverDialog from './coverDialog.js';

class Popup {
    overlayDiv = null;
    contentDiv = null;
    navigationDiv = null;
    loaded = false;
    loading = false;
    customizer = null; // Instance of Customizer
    options = {};
    coverOverlay = null; // Instance of CoverOverlay
    popupHoldCounter = 0; // Counter to manage popup hold state
    progressBar = null;
    progressFill = null;

    constructor() {
    }

    async load(options = {}) {
        document.body.style.overflow = 'hidden'; // Prevent scrolling when popup is open
        document.documentElement.style.overflow = 'hidden';
        document.body.classList.add('mscp-popup-open'); // Add class to body when popup is open

        this.options = options; // Store options for later use

        // Bestäm vilken session som ska laddas INNAN något annat
        // if (this.options.openInEdit && this.options.sessionID) {
        //     Storage.loadSession(this.options.sessionID);
        // } else if (!this.options.openInEdit) {
        // Detta kommer antingen återuppta 'currentSessionId' eller starta en ny.
        if (!Storage.getCurrentSessionId()) {
            Storage.startSession();
        }
        // }

        if (this.loaded && this.overlayDiv) {
            // If the popup is already loaded, just show it
            this.overlayDiv.style.display = 'flex';
            this.overlayDiv.classList.add('active');
            // Use the same logic as initial load to determine which step to show
            events.trigger('determineInitialStep', this.options);
            return;
        }
        if (!this.loading) {
            this.loading = true;

            // Load the popup content
            await this.init();
        }
        this.loaded = true;
        this.loading = false;

        this.ifStartOpenInfo(); // Check if we should open the info popup immediately
    }

    ifStartOpenInfo() {
        if (this.options?.info) {
            events.trigger('openAdditionalInfoPopup', {
                text_prop_name: this.options.info
            });
        }
    }

    isOpen() {
        return this.overlayDiv && this.overlayDiv.classList.contains('active');
    }

    async toggleAdjustMode(toggle = null) {
        if (!this.contentDiv) {
            return;
        }

        // Toggle adjust mode
        // If toggle is true, set adjust mode to true, if false, set it to false, if null, toggle the current state
        const lastState = this.contentDiv.classList.contains('adjust-mode');
        let newState = (typeof toggle === 'boolean') ? toggle : !lastState;

        if (lastState === newState) return; // No change

        // check if size is selected
        const size = Storage.get('size');
        if (!size && newState) {
            // Logger.warn('Cannot enter adjust mode without a selected size.');
            console.error('Cannot enter adjust mode without a selected size.');
            return;
        }
        // when exiting
        if (!newState) {
            events.trigger('updateNavigation');
        }

        this.contentDiv.classList.add('loading-adjust-mode');
        await events.trigger('preAdjustModeToggled', newState);
        this.contentDiv.classList.toggle('adjust-mode', newState);
        this.contentDiv.classList.remove('loading-adjust-mode');
    }

    async init() {
        await this.createPopup();

        this.customizer = new Customizer(this.contentDiv, this.options); // Initialize the customizer

        this.addEventListeners();
    }

    addEventListeners() {
        window.addEventListener('resize', this.resizeHandler);

        events.add('openAdditionalInfoPopup', (options) => {
            this.closeAdditionalInfo(); // Close any existing additional info popup
            // Create a new additional info popup
            this.additionalInfoPopup((wrapper) => {
                const infoText = textObj(options.text_prop_name);

                if (infoText && infoText.text) {
                    wrapper.innerHTML = infoText.text;
                    if (infoText.style) {
                        wrapper.classList.add(infoText.style);
                    }
                    TextParser.rows(wrapper);
                }
                else {
                    wrapper.innerHTML = _('No information available yet.');
                    return;
                }
            });
        });
        events.add('hidePopup', () => {
            this.hide();
        });

        events.add('closeAdditionalInfoPopup', () => {
            this.closeAdditionalInfo();
        });
        events.add('setPopupHold', (hold) => {
            if (this.overlayDiv) {
                if (hold) {
                    this.overlayDiv.classList.add('mscp-hold');
                    this.popupHoldCounter++;
                } else {
                    this.popupHoldCounter--;
                    if (this.popupHoldCounter < 0) this.popupHoldCounter = 0;
                    if (this.popupHoldCounter === 0) {
                        this.overlayDiv.classList.remove('mscp-hold');
                    }
                }
            }
        });
        events.add('toggleAdjustMode', (toggle = null) => {
            this.toggleAdjustMode(toggle); // Toggle adjust mode when the event is triggered
            window.Mindstore.DataLayer?.clickEvent('mscp_toggle_adjustmode', toggle ? 'on' : 'off');
        });
        events.add('stepChanged', (step) => { this.toggleAdjustMode(false); }); // Reset adjust mode when changing steps

        events.add('showLoadingOverlay', async (newStep, productPermalink = null) => {
            // newStep = newStep || 'general';
            if (!newStep) return;

            // Nytt steg 1: steps 1-3 have no intro overlay (it showed the old five dots).
            if (this.contentDiv?.classList.contains('mscp-up2-on') && ['upload', 'paper', 'presentation', 'size', 'gfxtext', 'retouch'].includes(newStep)) return;

            // Dölj progressbaren när proceed-overlayen visas (sparning klar)
            if (newStep === 'gotoProduct_proceed') {
                this.finishProgress();
            }

            // show overlay to hide loading
            this.coverOverlay?.show(newStep, productPermalink);
        });

        events.add('uploadProgress', (data) => {
            this.updateProgress(data.percent);
        });
        events.add('imageUploadStatusChanged', ({ id, newStatus }) => {
            if (newStatus === 'uploaded' || newStatus === 'failed') {
                this.finishProgress();
            }
        });

    }

    resizeHandler() {
        events.trigger('popupResize');
    }

    async createPopup() {

        // push history state to prevent back button closing the popup
        history.pushState({ popup: true }, '');

        if (this.overlayDiv) {
            // If the popup already exists, just return
            return;
        }


        /* create holder for popup */
        this.overlayDiv = document.createElement('div');
        this.overlayDiv.classList.add('mscp-popup-overlay');
        this.overlayDiv.style.display = 'flex';
        this.overlayDiv.classList.add('active');

        this.contentDiv = document.createElement('div');
        this.contentDiv.classList.add('mscp-popup-content');
        this.overlayDiv.appendChild(this.contentDiv);

        // Initialisera CoverOverlay
        // TODO: refactor in same way as CoverDialog
        this.coverOverlay = new CoverOverlay(this.contentDiv);

        // Initialisera CoverDialog (singleton)
        CoverDialog.init(this.contentDiv);

        // Create a close button only visible on upload step
        const closeButton = document.createElement('span');
        closeButton.classList.add('mscp-popup-close-button', 'mscp-upload-close-button', 'mscp-floating-button');
        closeButton.innerHTML = mindstore_object?.icon?.times; //textString('general_close_button', 'Close');
        closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.hide();
        });
        this.contentDiv.appendChild(closeButton);

        // Click outside the content (on the overlay backdrop) closes the popup.
        // Desktop only — on mobile the content fills the screen so there is no
        // backdrop to click, and an accidental edge-tap should not close it.
        this.overlayDiv.addEventListener('click', (e) => {
            if (e.target !== this.overlayDiv) return; // ignore clicks inside the content
            if (!window.matchMedia('(min-width: 768px)').matches) return; // desktop only
            this.hide();
        });

        if (this.overlayDiv) {
            document.body.appendChild(this.overlayDiv);
        }
        // this.coverOverlay?.show('firstload'); // TODO: change to 'upload' eller inget här???

        this.addProgressBar();


    }

    // --- PROGRESS BAR ---
    addProgressBar() {
        this.progressBar = document.createElement('div');
        this.progressBar.classList.add('mscp-progress-bar');

        this.progressFill = document.createElement('div');
        this.progressFill.classList.add('mscp-progress-fill');

        this.progressBar.appendChild(this.progressFill);
        this.contentDiv?.appendChild(this.progressBar);
    }

    updateProgress(percent) {
        if (!this.progressBar || !this.progressFill) return;

        // Visa baren om den är dold
        if (!this.progressBar.classList.contains('visible')) {
            this.progressBar.classList.add('visible');
        }

        this.progressFill.style.width = `${percent}%`;
    }

    finishProgress() {
        if (!this.progressBar) return;

        // Sätt till 100% för visuell bekräftelse
        if (this.progressFill) this.progressFill.style.width = '100%';

        // Dölj efter en kort stund
        setTimeout(() => {
            this.progressBar.classList.remove('visible');
            // Återställ width efter att den dolts (för nästa gång)
            setTimeout(() => {
                if (this.progressFill) this.progressFill.style.width = '0%';
            }, 300);
        }, 800);
    }

    hide() {
        // --- NY SESSIONS-LOGIK (START) ---
        // Om popupen stängs (och 'commit' inte har körts), 
        // är det en "avbryt"-åtgärd.
        // Detta rensar workspace OCH 'currentSessionId'.
        // Storage.abortSession();
        // --- NY SESSIONS-LOGIK (SLUT) ---


        this.overlayDiv?.classList.remove('active');
        // Reset body overflow to allow scrolling again
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.body.classList.remove('mscp-popup-open'); // Remove class from body when popup is closed

        events.trigger('popupHidden');
        events.trigger('toggleAdjustMode', false); // close adjust mode if open
        events.trigger('closeMainMenu');

        // Wait for the transition to finish before hiding the overlay
        setTimeout(() => {
            if (this.overlayDiv) {
                this.overlayDiv.style.display = 'none';
            }
            this.closeAdditionalInfo();
        }, 300);
    }

    destroy() {
        this.hide();
        if (this.coverOverlay) {
            this.coverOverlay?.destroy();
            this.coverOverlay = null;
        }
        if (this.overlayDiv) {
            this.overlayDiv.remove();
            this.overlayDiv = null;
            this.contentDiv = null;
            window.removeEventListener('resize', this.resizeHandler);
            this.loaded = false;
            this.loading = false;
        }
    }

    // create a div with full width and height inside the popup return the div to callback, create a close button at bottom full width
    additionalInfoPopup(callback) {
        if (!this.contentDiv) {
            console.error('Popup contentDiv is not initialized.');
            return;
        }
        // get scroll from the.contentDiv and set it to the top of the fullInfoWrapper
        const topMargin = this.contentDiv.scrollTop;

        const fullInfoWrapper = document.createElement('div');
        fullInfoWrapper.classList.add('mscp-full-info-wrapper');
        this.contentDiv.appendChild(fullInfoWrapper);
        this.contentDiv.style.overflow = 'hidden'; // Prevent scrolling in the content div
        fullInfoWrapper.style.marginTop = `${topMargin}px`; // Set the top margin to the current scroll position

        const fullInfoDiv = document.createElement('div');
        fullInfoDiv.classList.add('mscp-full-info', 'mscp-text-wrapper');
        fullInfoWrapper.appendChild(fullInfoDiv);

        // Create a close button
        const closeButton = document.createElement('span');
        closeButton.classList.add('mscp-popup-close-button', 'mscp-floating-button');
        closeButton.innerHTML = mindstore_object?.icon?.times; //textString('general_close_button', 'Close');
        closeButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.closeAdditionalInfo();
        });
        fullInfoWrapper.appendChild(closeButton);

        // Call the callback with the full info div
        if (typeof callback === 'function') {
            callback(fullInfoDiv);
        }


    }
    closeAdditionalInfo() {
        if (!this.contentDiv) {
            return;
        }
        this.contentDiv.style.overflow = 'auto'; // Allow scrolling in the content div again
        const fullInfoDiv = this.contentDiv.querySelector('.mscp-full-info-wrapper');
        if (fullInfoDiv) {
            fullInfoDiv.remove();
        }
    }



}

export default new Popup();
